import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ShoppingCart, 
  CreditCard, 
  BookOpen, 
  Link2, 
  CheckCircle2, 
  XCircle, 
  Settings,
  ExternalLink,
  ArrowRight,
  MessageCircle,
  Truck,
  Instagram,
  Mail,
  Lock,
  Store
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Integration card component
const IntegrationCard = ({ 
  name, 
  description, 
  icon: Icon, 
  iconBg, 
  status,
  statusLabel,
  path, 
  externalUrl,
  features,
  isLocked,
  upgradeRequired,
  onManage 
}) => {
  const navigate = useNavigate();
  
  const isConnected = status === 'connected';
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-wt-surface border rounded-xl p-6 transition-all ${
        isLocked
          ? 'border-wt-border opacity-70'
          : 'border-wt-border hover:border-wt-gold/30'
      }`}
      data-testid={`integration-card-${name.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className={`p-3 rounded-xl ${iconBg} ${isLocked ? 'grayscale' : ''}`}>
          <Icon className="h-8 w-8 text-white" />
        </div>
        {isLocked ? (
          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30" data-testid={`lock-badge-${name.toLowerCase().replace(/\s/g, '-')}`}>
            <Lock className="h-3 w-3 mr-1" /> Upgrade required
          </Badge>
        ) : (
          <Badge 
            className={isConnected 
              ? 'bg-green-500/20 text-green-400 border-green-500/30' 
              : 'bg-gray-500/20 text-gray-400 border-gray-500/30'
            }
          >
            {isConnected ? (
              <>
                <CheckCircle2 className="h-3 w-3 mr-1" />
                {statusLabel || 'Connected'}
              </>
            ) : (
              <><XCircle className="h-3 w-3 mr-1" /> Not Connected</>
            )}
          </Badge>
        )}
      </div>
      
      <h3 className="text-xl font-semibold text-wt-text-primary mb-2">{name}</h3>
      <p className="text-wt-text-muted text-sm mb-4">{description}</p>
      
      {features && features.length > 0 && (
        <div className="mb-4">
          <p className="text-xs text-wt-text-muted uppercase tracking-wide mb-2">Features</p>
          <div className="flex flex-wrap gap-2">
            {features.map((feature, idx) => (
              <span 
                key={idx} 
                className="text-xs bg-wt-paper px-2 py-1 rounded-md text-wt-text-muted"
              >
                {feature}
              </span>
            ))}
          </div>
        </div>
      )}
      
      <div className="flex gap-2 mt-4">
        {isLocked ? (
          <Button
            onClick={() => navigate('/billing')}
            className="flex-1 bg-amber-500 hover:bg-amber-400 text-black font-semibold"
            data-testid={`upgrade-${name.toLowerCase().replace(/\s/g, '-')}`}
          >
            <Lock className="h-4 w-4 mr-2" />
            Upgrade plan
          </Button>
        ) : (
          <Button
            onClick={() => navigate(path)}
            className="flex-1 bg-wt-gold hover:bg-wt-gold-dark text-black"
            data-testid={`manage-${name.toLowerCase().replace(/\s/g, '-')}`}
          >
            <Settings className="h-4 w-4 mr-2" />
            Manage
          </Button>
        )}
        {externalUrl && (
          <Button
            variant="outline"
            onClick={() => window.open(externalUrl, '_blank')}
            className="border-wt-border text-wt-text-muted hover:text-wt-text-primary"
          >
            <ExternalLink className="h-4 w-4" />
          </Button>
        )}
      </div>
    </motion.div>
  );
};

const IntegrationsPage = () => {
  const [shopifyStatus, setShopifyStatus] = useState('checking');
  const [flipkartStatus, setFlipkartStatus] = useState('checking');
  const [noonStatus, setNoonStatus] = useState('checking');
  const [danubeStatus, setDanubeStatus] = useState('checking');
  const [homecentreStatus, setHomecentreStatus] = useState('checking');
  const [carrefourStatus, setCarrefourStatus] = useState('checking');
  const [amazonCount, setAmazonCount] = useState({ in: 0, ae: 0 });
  const [mamoPayStatus, setMamoPayStatus] = useState('checking');
  const [zohoStatus, setZohoStatus] = useState('checking');
  const [whatsappStatus, setWhatsappStatus] = useState('checking');
  const [instagramStatus, setInstagramStatus] = useState('checking');
  const [stripeStatus, setStripeStatus] = useState('checking');
  const [delhiveryStatus, setDelhiveryStatus] = useState('checking');
  const [delhiveryCount, setDelhiveryCount] = useState(0);
  const [bluedartStatus, setBluedartStatus] = useState('checking');
  const [bluedartCount, setBluedartCount] = useState(0);
  const [dtdcStatus, setDtdcStatus] = useState('checking');
  const [dtdcCount, setDtdcCount] = useState(0);
  const [shiprocketStatus, setShiprocketStatus] = useState('checking');
  const [shiprocketCount, setShiprocketCount] = useState(0);
  const [customCourierStatus, setCustomCourierStatus] = useState('checking');
  const [customCourierCount, setCustomCourierCount] = useState(0);
  const [emailStatus, setEmailStatus] = useState('checking');
  const [emailCount, setEmailCount] = useState(0);
  // Plan-allowed integration keys, fetched once on mount. Empty array
  // until loaded → cards render unlocked optimistically; once the
  // response arrives, locked cards switch to "Upgrade required".
  const [allowedIntegrations, setAllowedIntegrations] = useState(null);
  
  useEffect(() => {
    checkIntegrationStatuses();
    // Pull plan-allowed integration keys so we can lock cards the
    // company hasn't paid for. Backend already 403s, but we owe the
    // user a clear "upgrade" CTA instead of a confusing toast on click.
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_BASE_URL}/company/plan-modules`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const list = res.data?.plan?.integrations;
        // No plan or undefined → treat as "everything allowed" so we
        // don't lock super-admins / legacy companies on a stale API.
        setAllowedIntegrations(Array.isArray(list) ? list : null);
      } catch (e) {
        setAllowedIntegrations(null);
      }
    })();
  }, []);

  // Helper: a card is locked when we know the plan AND the integration
  // key isn't in the allowed list. Unknown keys (or missing key) are
  // always allowed (defensive — never lock something we can't gate).
  // The wildcard `*` in `allowedIntegrations` means "all integrations"
  // (used by Enterprise plans) so we never gate when present.
  const isLocked = (key) => {
    if (!key) return false;
    if (allowedIntegrations === null) return false;
    if (allowedIntegrations.includes('*')) return false;
    return !allowedIntegrations.includes(key);
  };
  
  const checkIntegrationStatuses = async () => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    
    // Check Shopify status
    try {
      const shopifyRes = await axios.get(`${API_BASE_URL}/shopify/stores`, { headers });
      const activeStore = shopifyRes.data.stores?.find(s => s.is_active);
      setShopifyStatus(activeStore ? 'connected' : 'not_connected');
    } catch (error) {
      setShopifyStatus('not_connected');
    }

    // Check Flipkart status
    try {
      const flipkartRes = await axios.get(`${API_BASE_URL}/integrations/flipkart/status`, { headers });
      setFlipkartStatus(flipkartRes.data?.connected ? 'connected' : 'not_connected');
    } catch (error) {
      setFlipkartStatus('not_connected');
    }

    // Check noon status
    try {
      const noonRes = await axios.get(`${API_BASE_URL}/noon/status`, { headers });
      setNoonStatus(noonRes.data?.connected ? 'connected' : 'not_connected');
    } catch (error) {
      setNoonStatus('not_connected');
    }

    // Check Danube Home status
    try {
      const dhRes = await axios.get(`${API_BASE_URL}/danube/status`, { headers });
      setDanubeStatus(dhRes.data?.connected ? 'connected' : 'not_connected');
    } catch (error) {
      setDanubeStatus('not_connected');
    }

    // Check Home Centre status
    try {
      const hcRes = await axios.get(`${API_BASE_URL}/homecentre/status`, { headers });
      setHomecentreStatus(hcRes.data?.connected ? 'connected' : 'not_connected');
    } catch (error) {
      setHomecentreStatus('not_connected');
    }

    // Check Carrefour UAE status
    try {
      const cfRes = await axios.get(`${API_BASE_URL}/carrefour/status`, { headers });
      setCarrefourStatus(cfRes.data?.connected ? 'connected' : 'not_connected');
    } catch (error) {
      setCarrefourStatus('not_connected');
    }

    // Amazon — count active accounts across all countries
    try {
      const az = await axios.get(`${API_BASE_URL}/integrations/amazon/accounts`, { headers });
      const all = (az.data?.accounts || []).filter((a) => a.is_active);
      setAmazonCount({
        in: all.filter((a) => a.channel_type === 'amazon_in').length,
        ae: all.filter((a) => a.channel_type !== 'amazon_in').length, // everything else → counts into "other"
      });
    } catch (err) {
      setAmazonCount({ in: 0, ae: 0 });
    }
    
    // Check MamoPay status (use dedicated mamopay settings endpoint)
    try {
      const mamoRes = await axios.get(`${API_BASE_URL}/settings/mamopay`, { headers });
      setMamoPayStatus(mamoRes.data.configured ? 'connected' : 'not_connected');
    } catch (error) {
      setMamoPayStatus('not_connected');
    }
    
    // Check Zoho status
    try {
      const zohoRes = await axios.get(`${API_BASE_URL}/enterprise/zoho/status`, { headers });
      setZohoStatus(zohoRes.data.working ? 'connected' : 'not_connected');
    } catch (error) {
      setZohoStatus('not_connected');
    }
    
    // Check WhatsApp status
    try {
      const whatsappRes = await axios.get(`${API_BASE_URL}/whatsapp/settings`, { headers });
      setWhatsappStatus(whatsappRes.data.configured ? 'connected' : 'not_connected');
    } catch (error) {
      setWhatsappStatus('not_connected');
    }
    
    // Check Instagram status
    try {
      const instagramRes = await axios.get(`${API_BASE_URL}/instagram/account`, { headers });
      setInstagramStatus(instagramRes.data.connected ? 'connected' : 'not_connected');
    } catch (error) {
      setInstagramStatus('not_connected');
    }
    
    // Check Stripe status
    try {
      const stripeRes = await axios.get(`${API_BASE_URL}/tracking/stripe/status`, { headers });
      setStripeStatus(stripeRes.data.configured ? 'connected' : 'not_connected');
    } catch (error) {
      setStripeStatus('not_connected');
    }
    
    // Check Delhivery status
    try {
      const delhiveryRes = await axios.get(`${API_BASE_URL}/couriers/integrations/delhivery`, { headers });
      const count = delhiveryRes.data.count || delhiveryRes.data.integrations?.length || 0;
      setDelhiveryCount(count);
      setDelhiveryStatus(count > 0 ? 'connected' : 'not_connected');
    } catch (error) {
      setDelhiveryStatus('not_connected');
      setDelhiveryCount(0);
    }
    
    // Check Bluedart status
    try {
      const bluedartRes = await axios.get(`${API_BASE_URL}/couriers/integrations/bluedart`, { headers });
      const count = bluedartRes.data.count || bluedartRes.data.integrations?.length || 0;
      setBluedartCount(count);
      setBluedartStatus(count > 0 ? 'connected' : 'not_connected');
    } catch (error) {
      setBluedartStatus('not_connected');
      setBluedartCount(0);
    }
    
    // Check DTDC status
    try {
      const dtdcRes = await axios.get(`${API_BASE_URL}/couriers/integrations/dtdc`, { headers });
      const count = dtdcRes.data.count || dtdcRes.data.integrations?.length || 0;
      setDtdcCount(count);
      setDtdcStatus(count > 0 ? 'connected' : 'not_connected');
    } catch (error) {
      setDtdcStatus('not_connected');
      setDtdcCount(0);
    }
    
    // Check Shiprocket status
    try {
      const srRes = await axios.get(`${API_BASE_URL}/couriers/integrations/shiprocket`, { headers });
      const srCount = srRes.data.count || srRes.data.integrations?.length || 0;
      setShiprocketCount(srCount);
      setShiprocketStatus(srCount > 0 ? 'connected' : 'not_connected');
    } catch (error) {
      setShiprocketStatus('not_connected');
      setShiprocketCount(0);
    }
    
    // Check Custom Courier status
    try {
      const customRes = await axios.get(`${API_BASE_URL}/couriers/custom-couriers`, { headers });
      const count = customRes.data.integrations?.length || 0;
      setCustomCourierCount(count);
      setCustomCourierStatus(count > 0 ? 'connected' : 'not_connected');
    } catch (error) {
      setCustomCourierStatus('not_connected');
      setCustomCourierCount(0);
    }
    
    // Check Email status
    try {
      const emailRes = await axios.get(`${API_BASE_URL}/email/status`, { headers });
      setEmailCount(emailRes.data.account_count || 0);
      setEmailStatus(emailRes.data.connected ? 'connected' : 'not_connected');
    } catch (error) {
      setEmailStatus('not_connected');
      setEmailCount(0);
    }
  };
  
  const integrations = [
    {
      name: 'Shopify',
      description: 'Sync products, orders, inventory, and customers with your Shopify store in real-time.',
      icon: ShoppingCart,
      iconBg: 'bg-gradient-to-br from-green-500 to-green-600',
      status: shopifyStatus,
      key: 'shopify',
      path: '/integrations/shopify',
      externalUrl: 'https://shopify.com',
      features: ['Product Sync', 'Order Sync', 'Inventory Levels', 'Customer Data', 'Webhooks']
    },
    {
      name: 'Flipkart',
      description: 'Sync Flipkart orders + push sellable inventory. Auto-acknowledge, cancellation sync, multi-warehouse mapping.',
      icon: ShoppingCart,
      iconBg: 'bg-gradient-to-br from-blue-500 to-amber-400',
      status: flipkartStatus,
      key: 'flipkart',
      path: '/integrations/flipkart',
      externalUrl: 'https://seller.flipkart.com',
      features: ['Order Ingestion', 'Auto-Acknowledge', 'Inventory Push', 'Multi-Warehouse Mapping', 'Sandbox Support']
    },
    {
      name: 'noon',
      description: 'Sync inventory to noon warehouses, ingest FBPI orders, create shipments with noon AWBs, and list products with attribute templates.',
      icon: ShoppingCart,
      iconBg: 'bg-gradient-to-br from-yellow-400 to-yellow-600',
      status: noonStatus,
      key: 'noon',
      path: '/settings/noon',
      externalUrl: 'https://login.noon.partners',
      features: ['Warehouse Inventory Sync', 'Order Ingestion', 'AWB / QR Shipments', 'Product Listing', 'AI Arabic Content']
    },
    {
      name: 'Danube Home',
      description: 'Sync stock to GCC countries, auto-ingest & fulfil Seller Hub orders through your drivers, and invoice via Zoho Books net of commission.',
      icon: Store,
      iconBg: 'bg-gradient-to-br from-yellow-400 to-yellow-600',
      status: danubeStatus,
      key: 'danube',
      path: '/settings/danube',
      externalUrl: 'https://sellerhub.danubehome.com',
      features: ['GCC Inventory Sync', 'Order Ingestion & Auto-Accept', 'Driver Delivery + POD', 'Zoho Invoicing']
    },
    {
      name: 'Carrefour UAE',
      description: 'MAF Marketplace Partner API — price & stock sync, real-time order webhooks, and automatic Pack/Ship/Deliver pushes through your driver flow.',
      icon: Store,
      iconBg: 'bg-gradient-to-br from-blue-500 to-blue-700',
      status: carrefourStatus,
      key: 'carrefour',
      path: '/settings/carrefour',
      externalUrl: 'https://marketplace.carrefouruae.com/portal',
      features: ['Price & Stock Sync', 'Real-Time Order Webhooks', 'Pack / Ship / Deliver Pushes', 'Shopify Mirror']
    },
    {
      name: 'Home Centre',
      description: 'Landmark Group (Vin eRetail) — order ingestion, stock sync with virtual buffer, Shopify-relative price markup, and AWB / invoice / label pushes.',
      icon: Store,
      iconBg: 'bg-gradient-to-br from-orange-500 to-rose-600',
      status: homecentreStatus,
      key: 'homecentre',
      path: '/settings/homecentre',
      externalUrl: 'https://marketplace.carrefouruae.com',
      features: ['Order Ingestion', 'Stock Sync (+ Virtual)', 'Price Markup vs Shopify', 'AWB / Invoice / Label']
    },
    {
      name: 'Amazon Marketplaces',
      description: `17 countries supported • Connect-with-Amazon OAuth.${(amazonCount.in + amazonCount.ae) > 0 ? ` ${amazonCount.in + amazonCount.ae} account${(amazonCount.in + amazonCount.ae) > 1 ? 's' : ''} connected across all regions.` : ''}`,
      icon: ShoppingCart,
      iconBg: 'bg-gradient-to-br from-[#232F3E] to-[#FF9900]',
      status: (amazonCount.in + amazonCount.ae) > 0 ? 'connected' : 'not_connected',
      key: 'amazon',
      path: '/integrations/amazon',
      externalUrl: 'https://sellercentral.amazon.com',
      features: ['Multi-Country', 'Multi-Account', 'SaaS OAuth', 'MFN Order Sync', 'Confirm Shipment', 'Returns Polling']
    },
    {
      name: 'MamoPay',
      description: 'Accept online payments and generate payment links for COD orders.',
      icon: CreditCard,
      iconBg: 'bg-gradient-to-br from-purple-500 to-purple-600',
      status: mamoPayStatus,
      key: 'mamopay',
      path: '/integrations/mamopay',
      externalUrl: 'https://mamopay.com',
      features: ['Payment Links', 'COD Conversion', 'Auto-Confirmation', 'Transaction Tracking']
    },
    {
      name: 'Zoho Books',
      description: 'Sync purchase orders, invoices, and accounting data with Zoho Books.',
      icon: BookOpen,
      iconBg: 'bg-gradient-to-br from-red-500 to-red-600',
      status: zohoStatus,
      key: 'zoho',
      path: '/integrations/zoho',
      externalUrl: 'https://books.zoho.com',
      features: ['Purchase Orders', 'Bills', 'Vendor Sync', 'Chart of Accounts']
    },
    {
      name: 'WhatsApp Business',
      description: 'Send automated WhatsApp messages via Meta Cloud API. Build marketing journeys.',
      icon: MessageCircle,
      iconBg: 'bg-gradient-to-br from-green-400 to-green-500',
      status: whatsappStatus,
      key: 'whatsapp',
      path: '/integrations/whatsapp',
      externalUrl: 'https://business.whatsapp.com',
      features: ['Message Templates', 'Marketing Journeys', 'Delivery Notifications', 'Read Receipts']
    },
    {
      name: 'Instagram',
      description: 'Connect Instagram for DMs, chatbot automation, and auto-posting new products.',
      icon: Instagram,
      iconBg: 'bg-gradient-to-br from-purple-500 to-pink-500',
      status: instagramStatus,
      key: 'instagram',
      path: '/integrations/instagram',
      externalUrl: 'https://instagram.com',
      features: ['DM Inbox', 'Chatbot Automation', 'Auto-Posting', 'Story Mentions']
    },
    {
      name: 'Stripe',
      description: 'Accept customer tips and payments on order tracking pages.',
      icon: CreditCard,
      iconBg: 'bg-gradient-to-br from-indigo-500 to-purple-600',
      status: stripeStatus,
      key: 'stripe',
      path: '/integrations/stripe',
      externalUrl: 'https://stripe.com',
      features: ['Customer Tips', 'Payment Processing', 'Multi-Currency', 'Secure Checkout']
    },
    {
      name: 'Delhivery',
      description: delhiveryCount > 0 
        ? `${delhiveryCount} account${delhiveryCount > 1 ? 's' : ''} configured. Book shipments, generate AWB, and track deliveries.`
        : 'India\'s leading logistics provider. Book shipments, generate AWB, and track deliveries.',
      icon: Truck,
      iconBg: 'bg-gradient-to-br from-red-500 to-red-600',
      status: delhiveryStatus,
      statusLabel: delhiveryCount > 1 ? `${delhiveryCount} accounts` : null,
      key: 'delhivery',
      path: '/integrations/delhivery',
      externalUrl: 'https://www.delhivery.com',
      features: ['AWB Generation', 'Shipping Labels', 'Real-time Tracking', 'COD Support', 'Multiple Accounts']
    },
    {
      name: 'Bluedart',
      description: bluedartCount > 0 
        ? `${bluedartCount} account${bluedartCount > 1 ? 's' : ''} configured. Premium express delivery across India.`
        : 'Premium express delivery service by DHL. Air and ground shipping with guaranteed delivery times.',
      icon: Truck,
      iconBg: 'bg-gradient-to-br from-blue-500 to-blue-600',
      status: bluedartStatus,
      statusLabel: bluedartCount > 1 ? `${bluedartCount} accounts` : null,
      key: 'bluedart',
      path: '/integrations/bluedart',
      externalUrl: 'https://www.bluedart.com',
      features: ['Express Delivery', 'Air Freight', 'COD Support', 'Premium Service', 'Tracking']
    },
    {
      name: 'DTDC',
      description: dtdcCount > 0 
        ? `${dtdcCount} account${dtdcCount > 1 ? 's' : ''} configured. Nationwide courier with priority and economy options.`
        : 'India\'s largest courier network. Priority and economy delivery options with wide coverage.',
      icon: Truck,
      iconBg: 'bg-gradient-to-br from-orange-500 to-orange-600',
      status: dtdcStatus,
      statusLabel: dtdcCount > 1 ? `${dtdcCount} accounts` : null,
      key: 'dtdc',
      path: '/integrations/dtdc',
      externalUrl: 'https://www.dtdc.in',
      features: ['Pan-India Coverage', 'Priority Delivery', 'Economy Options', 'COD Support', 'Tracking']
    },
    {
      name: 'Shiprocket',
      description: shiprocketCount > 0
        ? `${shiprocketCount} account${shiprocketCount > 1 ? 's' : ''} configured. Courier aggregator with 25+ partners and live rates.`
        : 'India\'s largest courier aggregator. Book via 25+ courier partners with live rate comparison and auto courier assignment.',
      icon: Truck,
      iconBg: 'bg-gradient-to-br from-indigo-500 to-violet-600',
      status: shiprocketStatus,
      statusLabel: shiprocketCount > 1 ? `${shiprocketCount} accounts` : null,
      key: 'shiprocket',
      path: '/integrations/shiprocket',
      externalUrl: 'https://www.shiprocket.in',
      features: ['25+ Courier Partners', 'Live Rates', 'Auto Courier Assignment', 'COD Support', 'Tracking']
    },
    {
      name: 'Custom Courier',
      description: customCourierCount > 0 
        ? `${customCourierCount} courier${customCourierCount > 1 ? 's' : ''} configured. Add your own couriers without API.`
        : 'Add custom couriers without API integration. Upload AWB numbers and manage serviceability.',
      icon: Truck,
      iconBg: 'bg-gradient-to-br from-purple-500 to-pink-500',
      status: customCourierStatus,
      statusLabel: customCourierCount > 1 ? `${customCourierCount} couriers` : null,
      path: '/integrations/custom-courier',
      features: ['No API Required', 'AWB Upload', 'Manual Entry', 'Pincode Control', 'Custom Labels']
    },
    {
      name: 'Email',
      description: emailCount > 0 
        ? `${emailCount} account${emailCount > 1 ? 's' : ''} connected. Send and receive emails in your Inbox.`
        : 'Connect your email accounts via IMAP/SMTP or Gmail. Receive and send emails from your Inbox.',
      icon: Mail,
      iconBg: 'bg-gradient-to-br from-blue-500 to-cyan-500',
      status: emailStatus,
      statusLabel: emailCount > 1 ? `${emailCount} accounts` : null,
      key: 'gmail',
      path: '/integrations/email',
      externalUrl: null,
      features: ['IMAP/SMTP', 'Gmail OAuth', 'Domain Auth (SPF/DKIM/DMARC)', 'Email Forwarding', 'Inbox Integration']
    }
  ];
  
  const connectedCount = integrations.filter(i => i.status === 'connected').length;
  
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-playfair text-wt-text-primary">Integrations</h1>
          <p className="text-wt-text-muted mt-1">Connect and manage third-party services</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="bg-wt-surface border border-wt-border rounded-lg px-4 py-2">
            <span className="text-wt-text-muted text-sm">Connected: </span>
            <span className="text-wt-gold font-semibold">{connectedCount}/{integrations.length}</span>
          </div>
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-wt-surface border border-wt-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <CheckCircle2 className="h-5 w-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-wt-text-primary">{connectedCount}</p>
              <p className="text-sm text-wt-text-muted">Active Integrations</p>
            </div>
          </div>
        </div>
        <div className="bg-wt-surface border border-wt-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <Link2 className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-wt-text-primary">{integrations.length}</p>
              <p className="text-sm text-wt-text-muted">Available Integrations</p>
            </div>
          </div>
        </div>
        <div className="bg-wt-surface border border-wt-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-wt-gold/20 rounded-lg">
              <ArrowRight className="h-5 w-5 text-wt-gold" />
            </div>
            <div>
              <p className="text-2xl font-bold text-wt-text-primary">Real-time</p>
              <p className="text-sm text-wt-text-muted">Sync Status</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Integration Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {integrations.map((integration, idx) => (
          <IntegrationCard
            key={idx}
            {...integration}
            isLocked={integration.status !== 'connected' && isLocked(integration.key)}
          />
        ))}
      </div>
      
      {/* Coming Soon Section */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold text-wt-text-primary mb-4">Coming Soon</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { name: 'Twilio SMS', icon: '📱' },
            { name: 'Facebook Messenger', icon: '💬' },
            { name: 'Mailchimp', icon: '📧' }
          ].map((item, idx) => (
            <div 
              key={idx}
              className="bg-wt-surface/50 border border-wt-border/50 border-dashed rounded-xl p-4 text-center opacity-60"
            >
              <span className="text-2xl">{item.icon}</span>
              <p className="text-sm text-wt-text-muted mt-2">{item.name}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default IntegrationsPage;
